// CampusScene — the club's evolving postcard: the hero illustration grows on
// its own as the club levels up (TierRevealClip animates the change), with a
// weather ambience tint, a discreet works badge while construction runs, a
// condition alert pill when the courts need care, and a strip of "postcards"
// for recently finished works. Management lives below in the facility cards —
// the picture stays clean.

import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useGameStore } from '@/store/gameStore'
import {
  FACILITY_VISUALS, conditionStatus, getFacilityAsset, facilityAssetCandidates,
  type CampusSnapshot,
} from '@/config/campusConfig'
import {
  daysRemaining, constructionProgress, RECENT_COMPLETIONS_LIMIT,
  type CompletedWork,
} from '@/config/constructionConfig'
import { CampusHero } from './CampusHero'
import { HardHat, Sparkles } from 'lucide-react'

/** Ambience tint per weather (pure CSS, layered over the hero) */
const WEATHER_TINTS: Record<string, string> = {
  sunny: 'linear-gradient(180deg, rgba(251,146,60,0.06), transparent 55%)',
  cloudy: 'linear-gradient(180deg, rgba(148,163,184,0.14), rgba(148,163,184,0.05))',
  rainy: 'linear-gradient(180deg, rgba(56,120,190,0.22), rgba(30,58,95,0.12))',
  stormy: 'linear-gradient(180deg, rgba(15,23,42,0.38), rgba(30,41,59,0.22))',
}

/** How long a finished work stays in the postcards strip (in-game days) */
const POSTCARD_DAYS = 10

/** Discreet works chip over the hero corner: nearest job progress + count */
function WorksBadge() {
  const navigate = useNavigate()
  const constructionQueue = useGameStore(s => s.constructionQueue)
  const day = useGameStore(s => s.day)
  if (constructionQueue.length === 0) return null

  const next = [...constructionQueue].sort((a, b) => a.endDay - b.endDay)[0]
  const extra = constructionQueue.length - 1

  return (
    <button
      className="construction-ticker"
      onClick={() => navigate('/facilities?tab=edificios')}
      aria-label={`Obras en curso: ${next.label}`}
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        background: 'rgba(13,21,32,0.75)',
        border: '1px solid color-mix(in srgb, var(--warning) 40%, transparent)',
        color: '#F8FAFC',
      }}
    >
      <span className="construction-ticker-icon" style={{ color: 'var(--warning)' }}>
        <HardHat size={13} />
      </span>
      <span className="construction-ticker-body">
        <span className="construction-ticker-label" style={{ color: '#F8FAFC' }}>
          OBRAS{extra > 0 ? ` +${extra}` : ''} · {daysRemaining(next, day)}d
        </span>
        <span className="construction-ticker-bar">
          <span
            className="construction-ticker-fill"
            style={{ width: `${Math.round(constructionProgress(next, day) * 100)}%` }}
          />
        </span>
      </span>
    </button>
  )
}

/** Postcards of works finished in the last few days */
function RecentWorksStrip({ works }: { works: CompletedWork[] }) {
  const navigate = useNavigate()
  const region = useGameStore(s => s.region)
  if (works.length === 0) return null

  return (
    <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.6rem', overflowX: 'auto', paddingBottom: 2 }}>
      {works.map((work, i) => {
        const visual = FACILITY_VISUALS.find(v => v.id === work.visualId)
        const asset = visual ? getFacilityAsset(visual, work.tier) : null
        const thumb = visual && asset ? facilityAssetCandidates(visual, asset, region)[0] : undefined
        const isNew = work.kind === 'court' || work.kind === 'building'
        return (
          <motion.button
            key={`${work.visualId}-${work.day}-${i}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            onClick={() => navigate(visual?.manageRoute ?? '/facilities')}
            aria-label={`Ver ${work.label}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.3rem 0.55rem 0.3rem 0.3rem',
              border: '1px solid var(--border-default)',
              borderRadius: 10,
              background: 'var(--fill-soft)',
              cursor: 'pointer',
              flexShrink: 0,
              textAlign: 'left',
            }}
          >
            <span
              style={{
                width: 72,
                height: 54,
                borderRadius: 7,
                flexShrink: 0,
                background: visual
                  ? `linear-gradient(135deg, ${visual.placeholderGradient[0]}, ${visual.placeholderGradient[1]})`
                  : 'var(--fill-soft)',
                backgroundImage: thumb ? `url(${thumb})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <span style={{ minWidth: 0 }}>
              <span
                className={`pill ${isNew ? 'pill-success' : ''}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
              >
                <Sparkles size={9} /> {isNew ? 'Nuevo' : 'Mejorado'}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 130,
                }}
              >
                {work.label}
              </span>
            </span>
          </motion.button>
        )
      })}
    </div>
  )
}

export function CampusScene() {
  const clubLevel = useGameStore(s => s.clubLevel)
  const courts = useGameStore(s => s.courts)
  const buildings = useGameStore(s => s.buildings)
  const courtUpgrades = useGameStore(s => s.courtUpgrades)
  const currentWeather = useGameStore(s => s.currentWeather)
  const recentCompletions = useGameStore(s => s.recentCompletions)
  const day = useGameStore(s => s.day)

  const snapshot: CampusSnapshot = { clubLevel, courts, buildings, courtUpgrades }
  const tint = WEATHER_TINTS[currentWeather]
  const condition = conditionStatus(snapshot)
  const freshWorks = recentCompletions
    .filter(w => w.day >= day - POSTCARD_DAYS)
    .slice(0, RECENT_COMPLETIONS_LIMIT)

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <CampusHero />

        {/* Weather ambience tint over the illustration */}
        {tint && (
          <div style={{
            position: 'absolute', inset: 0,
            background: tint,
            borderRadius: 'inherit',
            pointerEvents: 'none',
          }} />
        )}

        <WorksBadge />

        {/* Court condition alert — only when something needs attention */}
        {condition && condition.tone !== 'ok' && (
          <span
            className={`pill ${condition.tone === 'danger' ? 'pill-danger' : 'pill-warning'}`}
            style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(13,21,32,0.8)' }}
          >
            {condition.label}
          </span>
        )}
      </div>

      <RecentWorksStrip works={freshWorks} />
    </div>
  )
}
